import React, { useState, Children } from 'react';
import { CheckIcon, HomeIcon, SearchIcon, CalendarIcon, BarChartIcon, KeyIcon, UsersIcon, ArrowRightIcon, MapPinIcon, HeartIcon, ClipboardCheckIcon, BuildingIcon, CompassIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const DollarSignIcon = props => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-57">
    <line x1="12" y1="1" x2="12" y2="23" data-id="element-58"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" data-id="element-59"></path>
  </svg>;
const HandshakeIcon = props => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-60">
    <path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z" data-id="element-61"></path>
  </svg>;
export const BuyersSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredLocation: '',
    budget: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    timeframe: '',
    additionalInfo: ''
  });
  const [activeTab, setActiveTab] = useState('first-time');
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formData);
    alert('Thank you for your submission! We will contact you soon.');
  };
  const propertyTypes = [{
    name: 'Residential',
    description: 'Single-family homes, condos, and apartments for personal living',
    icon: <HomeIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-62" />,
    featured: true
  }, {
    name: 'Luxury',
    description: 'High-end properties with premium amenities and locations',
    icon: <BuildingIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-63" />,
    featured: true
  }, {
    name: 'Investment',
    description: 'Properties with potential for rental income or appreciation',
    icon: <BarChartIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-64" />,
    featured: true
  }, {
    name: 'Vacation',
    description: 'Second homes in desirable locations for personal enjoyment',
    icon: <CompassIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-65" />
  }, {
    name: 'Commercial',
    description: 'Office spaces, retail locations, and mixed-use buildings',
    icon: <BuildingIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-66" />
  }, {
    name: 'Land',
    description: 'Undeveloped property for custom building or investment',
    icon: <MapPinIcon className="h-10 w-10 text-[#00A3E0]" data-id="element-67" />
  }];
  const buyerGuides = {
    'first-time': {
      title: 'First-Time Buyers',
      description: 'New to the home buying process? Our comprehensive guide walks you through every step, from saving for a down payment to closing on your first home.',
      points: ['Understanding mortgage pre-approval', 'First-time buyer assistance programs', 'Building credit for better rates', 'What to expect during closing'],
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1973&q=80'
    },
    investors: {
      title: 'Property Investors',
      description: 'Looking to build your real estate portfolio? Our investor guide provides strategies for identifying high-potential properties and maximizing returns.',
      points: ['Market analysis techniques', 'Cash flow calculation methods', 'Tax benefits for investors', 'Property management considerations'],
      image: 'https://images.unsplash.com/photo-1460317442991-0ec209397118?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    luxury: {
      title: 'Luxury Home Buyers',
      description: 'Seeking an exceptional property? Our luxury guide explores the nuances of high-end real estate, from exclusive listings to specialized financing options.',
      points: ['Access to off-market properties', 'Evaluating premium locations', 'Luxury home inspection checklist', 'Private banking relationships'],
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80'
    }
  };
  // Animation variants
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const listItem = {
    hidden: {
      opacity: 0,
      x: -20
    },
    visible: {
      opacity: 1,
      x: 0
    }
  };
  return <section className="w-full bg-[#f2f2ed]" data-id="element-68">
      {/* Hero Section */}
      <div className="pt-32 pb-20" data-id="element-69">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-70">
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6
        }} data-id="element-71">
            <h1 className="text-sm uppercase tracking-wider text-[#3a4a56] font-medium mb-6" data-id="element-72">
              BUYER SERVICES
            </h1>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-32" data-id="element-73">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-74">
              <h2 className="text-4xl md:text-5xl font-normal text-[#3a4a56] leading-tight mb-10" data-id="element-75">
                Your journey home starts here with personalized guidance every
                step of the way.
              </h2>
            </motion.div>
            <motion.div className="text-[#3a4a56]" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-76">
              <motion.p className="text-lg mb-8" variants={listItem} data-id="element-77">
                From first-time buyers to seasoned investors, we provide
                personalized guidance and access to exclusive properties that
                match your unique vision and lifestyle needs.
              </motion.p>
              <motion.p className="text-lg mb-8" variants={listItem} data-id="element-78">
                Our team of experts is dedicated to making your home buying
                experience seamless and successful, offering comprehensive
                support from initial search to final closing.
              </motion.p>
              <motion.p className="text-lg" variants={listItem} data-id="element-79">
                Discover the difference of working with professionals who truly
                understand your needs and have the expertise to help you find
                the perfect property.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Property Search Section */}
      <div className="py-20 border-t border-[#3a4a56]" data-id="element-80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-81">
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6
        }} data-id="element-82">
            <h6 className="text-sm uppercase tracking-wider text-[#3a4a56] font-medium mb-6" data-id="element-83">
              PROPERTY SEARCH
            </h6>
            <h2 className="text-4xl md:text-5xl font-normal text-[#3a4a56] leading-tight mb-10" data-id="element-84">
              Find Your Perfect Match
            </h2>
            <p className="text-lg text-[#3a4a56] max-w-3xl" data-id="element-85">
              Discover properties tailored to your preferences with our advanced
              search tools. Filter by location, price, features, and more to
              find homes that match your unique criteria.
            </p>
          </motion.div>
          {/* Search Form */}
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-86">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-87">
              <motion.div whileHover={{
              y: -5
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-88">
                <label className="block text-sm font-medium text-[#3a4a56] mb-1" data-id="element-89">
                  Location
                </label>
                <div className="relative" data-id="element-90">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-[#3a4a56]" data-id="element-91">
                    <MapPinIcon className="h-5 w-5" data-id="element-92" />
                  </span>
                  <input type="text" placeholder="City, neighborhood, or ZIP" className="w-full pl-10 px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-93" />
                </div>
              </motion.div>
              <motion.div whileHover={{
              y: -5
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-94">
                <label className="block text-sm font-medium text-[#3a4a56] mb-1" data-id="element-95">
                  Property Type
                </label>
                <select className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-96">
                  <option value="" data-id="element-97">Any Property Type</option>
                  <option value="house" data-id="element-98">House</option>
                  <option value="condo" data-id="element-99">Condo</option>
                  <option value="townhouse" data-id="element-100">Townhouse</option>
                  <option value="apartment" data-id="element-101">Apartment</option>
                  <option value="land" data-id="element-102">Land</option>
                </select>
              </motion.div>
              <motion.div whileHover={{
              y: -5
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-103">
                <label className="block text-sm font-medium text-[#3a4a56] mb-1" data-id="element-104">
                  Price Range
                </label>
                <select className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-105">
                  <option value="" data-id="element-106">Any Price</option>
                  <option value="100000-300000" data-id="element-107">$100,000 - $300,000</option>
                  <option value="300000-500000" data-id="element-108">$300,000 - $500,000</option>
                  <option value="500000-750000" data-id="element-109">$500,000 - $750,000</option>
                  <option value="750000-1000000" data-id="element-110">$750,000 - $1,000,000</option>
                  <option value="1000000+" data-id="element-111">$1,000,000+</option>
                </select>
              </motion.div>
              <motion.div className="flex items-end" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} transition={{
              type: 'spring',
              stiffness: 400
            }} data-id="element-112">
                <button className="w-full bg-[#3a4a56] hover:bg-[#2a3a46] text-white font-medium py-3 px-6 transition duration-200 inline-flex items-center justify-center" data-id="element-113">
                  <SearchIcon className="mr-2 h-5 w-5" data-id="element-114" />
                  Search Properties
                </button>
              </motion.div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2" data-id="element-115">
              <span className="text-sm font-medium text-[#3a4a56] mr-2" data-id="element-116">
                Popular:
              </span>
              <motion.a href="#" className="text-sm text-[#00A3E0] border-b border-[#00A3E0] hover:border-[#0089BD] transition" whileHover={{
              y: -2,
              color: '#0089BD'
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-117">
                Waterfront
              </motion.a>
              <motion.a href="#" className="text-sm text-[#00A3E0] border-b border-[#00A3E0] hover:border-[#0089BD] transition mx-4" whileHover={{
              y: -2,
              color: '#0089BD'
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-118">
                New Construction
              </motion.a>
              <motion.a href="#" className="text-sm text-[#00A3E0] border-b border-[#00A3E0] hover:border-[#0089BD] transition" whileHover={{
              y: -2,
              color: '#0089BD'
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-119">
                Pool
              </motion.a>
              <motion.a href="#" className="text-sm text-[#00A3E0] border-b border-[#00A3E0] hover:border-[#0089BD] transition mx-4" whileHover={{
              y: -2,
              color: '#0089BD'
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-120">
                Luxury
              </motion.a>
              <motion.a href="#" className="text-sm text-[#00A3E0] border-b border-[#00A3E0] hover:border-[#0089BD] transition" whileHover={{
              y: -2,
              color: '#0089BD'
            }} transition={{
              type: 'spring',
              stiffness: 300
            }} data-id="element-121">
                Investment
              </motion.a>
            </div>
          </motion.div>
          {/* Property Types */}
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-122">
            <h3 className="text-3xl font-normal text-[#3a4a56] mb-8" data-id="element-123">
              Explore Property Types
            </h3>
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-124">
              {propertyTypes.filter(type => type.featured).map((type, index) => <motion.div key={index} className="border-b border-[#3a4a56] pb-8" variants={listItem} data-id="element-125">
                    <div className="mb-6" data-id="element-126">{type.icon}</div>
                    <h4 className="text-xl font-normal text-[#3a4a56] mb-3" data-id="element-127">
                      {type.name}
                    </h4>
                    <p className="text-[#3a4a56] mb-6" data-id="element-128">{type.description}</p>
                    <motion.a href="#" className="text-[#00A3E0] font-medium hover:text-[#0089BD] inline-flex items-center" whileHover={{
                x: 5
              }} transition={{
                duration: 0.2
              }} data-id="element-129">
                      Browse {type.name} Properties
                      <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-130" />
                    </motion.a>
                  </motion.div>)}
            </motion.div>
          </motion.div>
          {/* View All Button */}
          <motion.div className="mb-12 text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-131">
            <motion.a href="#" className="bg-[#3a4a56] hover:bg-[#2a3a46] text-white font-medium py-3 px-8 transition duration-200" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 400
          }} data-id="element-132">
              View All Properties
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Buyer Guides Section */}
      <div className="py-20 border-t border-[#3a4a56]" data-id="element-133">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134">
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6
        }} data-id="element-135">
            <h6 className="text-sm uppercase tracking-wider text-[#3a4a56] font-medium mb-6" data-id="element-136">
              BUYER RESOURCES
            </h6>
            <h2 className="text-4xl md:text-5xl font-normal text-[#3a4a56] leading-tight mb-10" data-id="element-137">
              Expert Guidance for Every Buyer
            </h2>
            <p className="text-lg text-[#3a4a56] max-w-3xl" data-id="element-138">
              Whether you're purchasing your first home or expanding your
              investment portfolio, our specialized guides provide valuable
              insights for your unique situation.
            </p>
          </motion.div>
          {/* Tabs */}
          <motion.div className="flex flex-wrap mb-8 border-b border-[#3a4a56]" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-139">
            <motion.button onClick={() => setActiveTab('first-time')} className={`px-6 py-3 font-medium text-[#3a4a56] ${activeTab === 'first-time' ? 'border-b-2 border-[#00A3E0]' : ''}`} whileHover={{
            y: -3
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-140">
              First-Time Buyers
            </motion.button>
            <motion.button onClick={() => setActiveTab('investors')} className={`px-6 py-3 font-medium text-[#3a4a56] ${activeTab === 'investors' ? 'border-b-2 border-[#00A3E0]' : ''}`} whileHover={{
            y: -3
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-141">
              Investors
            </motion.button>
            <motion.button onClick={() => setActiveTab('luxury')} className={`px-6 py-3 font-medium text-[#3a4a56] ${activeTab === 'luxury' ? 'border-b-2 border-[#00A3E0]' : ''}`} whileHover={{
            y: -3
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-142">
              Luxury Buyers
            </motion.button>
          </motion.div>
          {/* Tab Content */}
          <motion.div className="border-b border-[#3a4a56] pb-16" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5
        }} key={activeTab} data-id="element-143">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-144">
              <div data-id="element-145">
                <motion.h3 className="text-3xl font-normal text-[#3a4a56] mb-4" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5
              }} data-id="element-146">
                  {buyerGuides[activeTab].title}
                </motion.h3>
                <motion.p className="text-[#3a4a56] mb-8" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.1
              }} data-id="element-147">
                  {buyerGuides[activeTab].description}
                </motion.p>
                <motion.ul className="mb-8 space-y-3" initial="hidden" animate="visible" variants={staggerContainer} data-id="element-148">
                  {buyerGuides[activeTab].points.map((point, index) => <motion.li key={index} className="flex items-start" variants={listItem} transition={{
                  duration: 0.3,
                  delay: index * 0.1
                }} data-id="element-149">
                      <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-0.5 flex-shrink-0" data-id="element-150" />
                      <span className="text-[#3a4a56]" data-id="element-151">{point}</span>
                    </motion.li>)}
                </motion.ul>
                <motion.a href="#" className="bg-[#3a4a56] hover:bg-[#2a3a46] text-white font-medium py-3 px-8 transition duration-200 inline-flex items-center" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.5,
                delay: 0.4
              }} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-152">
                  Download Free Guide
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-153" />
                </motion.a>
              </div>
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} whileHover={{
              scale: 1.03
            }} data-id="element-154">
                <img src={buyerGuides[activeTab].image} alt={buyerGuides[activeTab].title} className="w-full h-auto" data-id="element-155" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Buyer Journey */}
      <div className="py-20 border-t border-[#3a4a56]" data-id="element-156">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-157">
          <motion.div className="mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} transition={{
          duration: 0.6
        }} data-id="element-158">
            <h6 className="text-sm uppercase tracking-wider text-[#3a4a56] font-medium mb-6" data-id="element-159">
              YOUR JOURNEY
            </h6>
            <h2 className="text-4xl md:text-5xl font-normal text-[#3a4a56] leading-tight mb-10" data-id="element-160">
              The Path to Your New Home
            </h2>
            <p className="text-lg text-[#3a4a56] max-w-3xl" data-id="element-161">
              We make the home buying process simple and straightforward. Here's
              what you can expect when working with our expert team.
            </p>
          </motion.div>
          <div className="space-y-20" data-id="element-162">
            {/* Step 1 */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-163">
              <div data-id="element-164">
                <h3 className="text-3xl font-normal text-[#3a4a56] mb-6" data-id="element-165">
                  1. Initial Consultation
                </h3>
                <p className="text-lg text-[#3a4a56] mb-8" data-id="element-166">
                  We start by understanding your needs, preferences, and budget
                  to create a personalized home buying strategy.
                </p>
                <motion.ul className="space-y-3" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} data-id="element-167">
                  <motion.li className="flex items-start" variants={listItem} data-id="element-168">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-169" />
                    <span className="text-[#3a4a56]" data-id="element-170">
                      In-depth needs assessment
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-171">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-172" />
                    <span className="text-[#3a4a56]" data-id="element-173">
                      Budget planning assistance
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-174">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-175" />
                    <span className="text-[#3a4a56]" data-id="element-176">
                      Personalized search criteria
                    </span>
                  </motion.li>
                </motion.ul>
              </div>
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} whileHover={{
              scale: 1.03
            }} data-id="element-177">
                <img src="https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80" alt="Consultation" className="w-full h-auto" data-id="element-178" />
              </motion.div>
            </motion.div>
            {/* Step 2 */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-179">
              <motion.div className="order-2 lg:order-1" initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} whileHover={{
              scale: 1.03
            }} data-id="element-180">
                <img src="https://images.unsplash.com/photo-1560520031-5a62c5ec8347?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80" alt="Property Search" className="w-full h-auto" data-id="element-181" />
              </motion.div>
              <div className="order-1 lg:order-2" data-id="element-182">
                <h3 className="text-3xl font-normal text-[#3a4a56] mb-6" data-id="element-183">
                  2. Property Search & Tours
                </h3>
                <p className="text-lg text-[#3a4a56] mb-8" data-id="element-184">
                  We'll curate a selection of properties that match your
                  criteria and schedule personalized tours of your top choices.
                </p>
                <motion.ul className="space-y-3" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} data-id="element-185">
                  <motion.li className="flex items-start" variants={listItem} data-id="element-186">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-187" />
                    <span className="text-[#3a4a56]" data-id="element-188">
                      Curated property selections
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-189">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-190" />
                    <span className="text-[#3a4a56]" data-id="element-191">
                      Personalized property tours
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-192">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-193" />
                    <span className="text-[#3a4a56]" data-id="element-194">
                      Expert neighborhood insights
                    </span>
                  </motion.li>
                </motion.ul>
              </div>
            </motion.div>
            {/* Step 3 */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-195">
              <div data-id="element-196">
                <h3 className="text-3xl font-normal text-[#3a4a56] mb-6" data-id="element-197">
                  3. Offer & Negotiation
                </h3>
                <p className="text-lg text-[#3a4a56] mb-8" data-id="element-198">
                  When you find the perfect home, we'll help you prepare a
                  competitive offer and negotiate the best possible terms.
                </p>
                <motion.ul className="space-y-3" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} data-id="element-199">
                  <motion.li className="flex items-start" variants={listItem} data-id="element-200">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-201" />
                    <span className="text-[#3a4a56]" data-id="element-202">
                      Strategic offer preparation
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-203">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-204" />
                    <span className="text-[#3a4a56]" data-id="element-205">
                      Expert negotiation tactics
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-206">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-207" />
                    <span className="text-[#3a4a56]" data-id="element-208">
                      Contract review and guidance
                    </span>
                  </motion.li>
                </motion.ul>
              </div>
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} whileHover={{
              scale: 1.03
            }} data-id="element-209">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Negotiation" className="w-full h-auto" data-id="element-210" />
              </motion.div>
            </motion.div>
            {/* Step 4 */}
            <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-211">
              <motion.div className="order-2 lg:order-1" initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} whileHover={{
              scale: 1.03
            }} data-id="element-212">
                <img src="https://images.unsplash.com/photo-1460574283810-2aab119d8511?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Closing" className="w-full h-auto" data-id="element-213" />
              </motion.div>
              <div className="order-1 lg:order-2" data-id="element-214">
                <h3 className="text-3xl font-normal text-[#3a4a56] mb-6" data-id="element-215">
                  4. Closing & Beyond
                </h3>
                <p className="text-lg text-[#3a4a56] mb-8" data-id="element-216">
                  We'll guide you through inspections, paperwork, and closing
                  procedures, then continue to support you after you've moved
                  in.
                </p>
                <motion.ul className="space-y-3" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} data-id="element-217">
                  <motion.li className="flex items-start" variants={listItem} data-id="element-218">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-219" />
                    <span className="text-[#3a4a56]" data-id="element-220">
                      Inspection coordination
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-221">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-222" />
                    <span className="text-[#3a4a56]" data-id="element-223">
                      Closing process management
                    </span>
                  </motion.li>
                  <motion.li className="flex items-start" variants={listItem} data-id="element-224">
                    <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-225" />
                    <span className="text-[#3a4a56]" data-id="element-226">
                      Post-purchase support
                    </span>
                  </motion.li>
                </motion.ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="py-20 border-t border-[#3a4a56]" data-id="element-227">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-228">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-32" data-id="element-229">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6
          }} data-id="element-230">
              <h2 className="text-4xl font-normal text-[#3a4a56] leading-tight mb-10" data-id="element-231">
                Tell us about your dream home
              </h2>
              <p className="text-lg text-[#3a4a56] mb-8" data-id="element-232">
                Share your preferences and requirements, and our expert team
                will create a personalized property search tailored to your
                needs. We'll connect you with homes that match your vision and
                lifestyle.
              </p>
              <motion.ul className="space-y-4 mb-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} data-id="element-233">
                <motion.li className="flex items-start" variants={listItem} data-id="element-234">
                  <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-235" />
                  <span className="text-[#3a4a56] text-lg" data-id="element-236">
                    Personalized property matches
                  </span>
                </motion.li>
                <motion.li className="flex items-start" variants={listItem} data-id="element-237">
                  <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-238" />
                  <span className="text-[#3a4a56] text-lg" data-id="element-239">
                    Access to exclusive listings
                  </span>
                </motion.li>
                <motion.li className="flex items-start" variants={listItem} data-id="element-240">
                  <CheckIcon className="h-5 w-5 text-[#00A3E0] mr-3 mt-1 flex-shrink-0" data-id="element-241" />
                  <span className="text-[#3a4a56] text-lg" data-id="element-242">
                    Step-by-step buying guidance
                  </span>
                </motion.li>
              </motion.ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            duration: 0.6,
            delay: 0.3
          }} data-id="element-243">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-244">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-245">
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-246">
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" placeholder="Full Name" required data-id="element-247" />
                  </motion.div>
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-248">
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" placeholder="Email Address" required data-id="element-249" />
                  </motion.div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-250">
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-251">
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" placeholder="Phone Number" required data-id="element-252" />
                  </motion.div>
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-253">
                    <input type="text" id="preferredLocation" name="preferredLocation" value={formData.preferredLocation} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" placeholder="Preferred Location(s)" data-id="element-254" />
                  </motion.div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-255">
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-256">
                    <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" required data-id="element-257">
                      <option value="" data-id="element-258">Select Budget Range</option>
                      <option value="Under $200,000" data-id="element-259">Under $200,000</option>
                      <option value="$200,000 - $350,000" data-id="element-260">
                        $200,000 - $350,000
                      </option>
                      <option value="$350,000 - $500,000" data-id="element-261">
                        $350,000 - $500,000
                      </option>
                      <option value="$500,000 - $750,000" data-id="element-262">
                        $500,000 - $750,000
                      </option>
                      <option value="$750,000 - $1,000,000" data-id="element-263">
                        $750,000 - $1,000,000
                      </option>
                      <option value="Over $1,000,000" data-id="element-264">Over $1,000,000</option>
                    </select>
                  </motion.div>
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-265">
                    <select id="propertyType" name="propertyType" value={formData.propertyType} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" required data-id="element-266">
                      <option value="" data-id="element-267">Select Property Type</option>
                      <option value="Single Family Home" data-id="element-268">
                        Single Family Home
                      </option>
                      <option value="Townhouse" data-id="element-269">Townhouse</option>
                      <option value="Condo" data-id="element-270">Condo</option>
                      <option value="Apartment" data-id="element-271">Apartment</option>
                      <option value="Multi-Family" data-id="element-272">Multi-Family</option>
                      <option value="Land" data-id="element-273">Land</option>
                      <option value="Other" data-id="element-274">Other</option>
                    </select>
                  </motion.div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-275">
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-276">
                    <select id="bedrooms" name="bedrooms" value={formData.bedrooms} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-277">
                      <option value="" data-id="element-278">Bedrooms (Any)</option>
                      <option value="1" data-id="element-279">1+</option>
                      <option value="2" data-id="element-280">2+</option>
                      <option value="3" data-id="element-281">3+</option>
                      <option value="4" data-id="element-282">4+</option>
                      <option value="5" data-id="element-283">5+</option>
                    </select>
                  </motion.div>
                  <motion.div whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-284">
                    <select id="bathrooms" name="bathrooms" value={formData.bathrooms} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-285">
                      <option value="" data-id="element-286">Bathrooms (Any)</option>
                      <option value="1" data-id="element-287">1+</option>
                      <option value="1.5" data-id="element-288">1.5+</option>
                      <option value="2" data-id="element-289">2+</option>
                      <option value="2.5" data-id="element-290">2.5+</option>
                      <option value="3" data-id="element-291">3+</option>
                    </select>
                  </motion.div>
                </div>
                <motion.div whileHover={{
                y: -5
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} data-id="element-292">
                  <select id="timeframe" name="timeframe" value={formData.timeframe} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" data-id="element-293">
                    <option value="" data-id="element-294">Buying Timeframe</option>
                    <option value="Immediately" data-id="element-295">Immediately</option>
                    <option value="1-3 months" data-id="element-296">1-3 months</option>
                    <option value="3-6 months" data-id="element-297">3-6 months</option>
                    <option value="6+ months" data-id="element-298">6+ months</option>
                    <option value="Just browsing" data-id="element-299">Just browsing</option>
                  </select>
                </motion.div>
                <motion.div whileHover={{
                y: -5
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} data-id="element-300">
                  <textarea id="additionalInfo" name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} className="w-full px-4 py-3 border-b border-[#3a4a56] bg-transparent focus:outline-none focus:border-[#00A3E0]" rows={4} placeholder="Tell us about any specific features or requirements you're looking for in your dream home." data-id="element-301"></textarea>
                </motion.div>
                <div data-id="element-302">
                  <motion.button type="submit" className="bg-[#3a4a56] hover:bg-[#2a3a46] text-white font-medium py-3 px-8 transition duration-200" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} transition={{
                  type: 'spring',
                  stiffness: 400
                }} data-id="element-303">
                    Submit Request
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
};